import './index.css'
import { useEffect, useState, useRef } from 'react'
import { Search, Shield, RefreshCw, X, Check, Send, MessageSquare, Users } from 'lucide-react'
import { useAdminPanel } from '../../hooks/useAdminPanel'
import { useAdminMessages } from '../../hooks/useAdminMessages'
import { useAdminBroadcasts } from '../../hooks/useAdminBroadcasts'
import type { AdminUser, PlanTier } from '../../types'
import { getToastErrorMessage, useToast } from '../Toast'

type Tab = 'users' | 'whatsapp'
type BillingCycle = 'monthly' | 'annual'

interface GrantModalProps {
  user: AdminUser
  onConfirm: (planTier: Exclude<PlanTier, 'free'>, billingCycle: BillingCycle, currentPeriodEnd: string | null) => Promise<void>
  onClose: () => void
}

function GrantModal({ user, onConfirm, onClose }: GrantModalProps) {
  const [planTier, setPlanTier] = useState<Exclude<PlanTier, 'free'>>('pro')
  const [billingCycle, setBillingCycle] = useState<BillingCycle>('monthly')
  const [hasExpiry, setHasExpiry] = useState(false)
  const [expiryDate, setExpiryDate] = useState('')
  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  async function handleConfirm() {
    setErr(null)
    setLoading(true)
    try {
      const end = hasExpiry && expiryDate ? new Date(expiryDate).toISOString() : null
      await onConfirm(planTier, billingCycle, end)
      onClose()
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Erro ao ativar plano')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="bc-admin-modal-overlay" onClick={onClose}>
      <div className="bc-admin-modal" onClick={(e) => e.stopPropagation()}>
        <div className="bc-admin-modal__header">
          <h3>Ativar plano</h3>
          <button className="bc-admin-modal__close" onClick={onClose}><X size={16} /></button>
        </div>

        <div className="bc-admin-modal__user">
          <span className="bc-admin-modal__email">{user.email}</span>
          {user.full_name && <span className="bc-admin-modal__name">{user.full_name}</span>}
        </div>

        <div className="bc-admin-field">
          <label className="bc-admin-label">Plano</label>
          <div className="bc-admin-radio-group">
            {(['pro', 'casal'] as const).map((tier) => (
              <label key={tier} className={'bc-admin-radio' + (planTier === tier ? ' is-active' : '')}>
                <input
                  type="radio"
                  name="planTier"
                  value={tier}
                  checked={planTier === tier}
                  onChange={() => setPlanTier(tier)}
                />
                {tier === 'pro' ? 'Pro' : 'Casal'}
              </label>
            ))}
          </div>
        </div>

        <div className="bc-admin-field">
          <label className="bc-admin-label">Ciclo de cobrança</label>
          <div className="bc-admin-radio-group">
            {([['monthly', 'Mensal'], ['annual', 'Anual']] as const).map(([val, label]) => (
              <label key={val} className={'bc-admin-radio' + (billingCycle === val ? ' is-active' : '')}>
                <input
                  type="radio"
                  name="billingCycle"
                  value={val}
                  checked={billingCycle === val}
                  onChange={() => setBillingCycle(val)}
                />
                {label}
              </label>
            ))}
          </div>
        </div>

        <div className="bc-admin-field">
          <label className="bc-admin-label">Validade</label>
          <div className="bc-admin-radio-group">
            <label className={'bc-admin-radio' + (!hasExpiry ? ' is-active' : '')}>
              <input type="radio" name="expiry" checked={!hasExpiry} onChange={() => setHasExpiry(false)} />
              Indefinido
            </label>
            <label className={'bc-admin-radio' + (hasExpiry ? ' is-active' : '')}>
              <input type="radio" name="expiry" checked={hasExpiry} onChange={() => setHasExpiry(true)} />
              Data específica
            </label>
          </div>
          {hasExpiry && (
            <input
              className="bc-admin-date"
              type="date"
              value={expiryDate}
              onChange={(e) => setExpiryDate(e.target.value)}
              min={new Date().toISOString().slice(0, 10)}
            />
          )}
        </div>

        {err && <p className="bc-admin-error">{err}</p>}

        <div className="bc-admin-modal__actions">
          <button className="bc-admin-btn bc-admin-btn--ghost" onClick={onClose} disabled={loading}>
            Cancelar
          </button>
          <button className="bc-admin-btn bc-admin-btn--primary" onClick={handleConfirm} disabled={loading}>
            {loading ? 'Salvando…' : 'Confirmar'}
          </button>
        </div>
      </div>
    </div>
  )
}

interface RevokeConfirmProps {
  user: AdminUser
  onConfirm: () => Promise<void>
  onClose: () => void
}

function RevokeConfirm({ user, onConfirm, onClose }: RevokeConfirmProps) {
  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  async function handleConfirm() {
    setErr(null)
    setLoading(true)
    try {
      await onConfirm()
      onClose()
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Erro ao revogar plano')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="bc-admin-modal-overlay" onClick={onClose}>
      <div className="bc-admin-modal" onClick={(e) => e.stopPropagation()}>
        <div className="bc-admin-modal__header">
          <h3>Revogar plano</h3>
          <button className="bc-admin-modal__close" onClick={onClose}><X size={16} /></button>
        </div>
        <p className="bc-admin-modal__body">
          Tornar <strong>{user.email}</strong> Free? O acesso às features pagas será removido imediatamente.
        </p>
        {err && <p className="bc-admin-error">{err}</p>}
        <div className="bc-admin-modal__actions">
          <button className="bc-admin-btn bc-admin-btn--ghost" onClick={onClose} disabled={loading}>
            Cancelar
          </button>
          <button className="bc-admin-btn bc-admin-btn--danger" onClick={handleConfirm} disabled={loading}>
            {loading ? 'Revogando…' : 'Tornar Free'}
          </button>
        </div>
      </div>
    </div>
  )
}

function planBadge(tier: string) {
  if (tier === 'pro') return <span className="bc-badge bc-badge--pro">Pro</span>
  if (tier === 'casal') return <span className="bc-badge bc-badge--casal">Casal</span>
  return <span className="bc-admin-badge-free">Free</span>
}

function formatExpiry(date: string | null) {
  if (!date) return '—'
  return new Date(date).toLocaleDateString('pt-BR')
}

function formatDateTime(iso: string) {
  return new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit', year: '2-digit',
    hour: '2-digit', minute: '2-digit',
  })
}

export default function AdminPanel({ adminEmail }: { adminEmail: string }) {
  const globalToast = useToast()
  const [activeTab, setActiveTab] = useState<Tab>('users')
  const { users, total, loading, error, fetchUsers, searchUsers, setPlan, revokePlan } = useAdminPanel()
  const { sendMessage, loading: msgLoading, sendResults } = useAdminMessages()
  const { broadcasts, loading: bcastLoading, fetchBroadcasts } = useAdminBroadcasts()
  const visibleUsers = users.filter((u) => u.email?.toLowerCase() !== adminEmail.toLowerCase())
  const [query, setQuery] = useState('')
  const [page, setPage] = useState(1)
  const [grantTarget, setGrantTarget] = useState<AdminUser | null>(null)
  const [revokeTarget, setRevokeTarget] = useState<AdminUser | null>(null)
  const [msgText, setMsgText] = useState('')
  const [expandedBroadcast, setExpandedBroadcast] = useState<string | null>(null)
  const searchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    fetchUsers(1)
  }, [fetchUsers])

  useEffect(() => {
    if (activeTab === 'whatsapp') fetchBroadcasts()
  }, [activeTab, fetchBroadcasts])

  function handleSearch(val: string) {
    setQuery(val)
    if (searchTimeout.current) clearTimeout(searchTimeout.current)
    searchTimeout.current = setTimeout(() => {
      if (val.trim()) {
        searchUsers(val)
      } else {
        setPage(1)
        fetchUsers(1)
      }
    }, 400)
  }

  function handlePageChange(next: number) {
    setPage(next)
    fetchUsers(next)
  }

  const totalPages = Math.max(1, Math.ceil(total / 50))

  return (
    <main className="bc-admin">
      <header className="bc-admin__header">
        <div className="bc-admin__title">
          <Shield size={20} />
          <h1>Painel Admin</h1>
          <span className="bc-admin__badge">hellobagcash</span>
        </div>
        <button
          className="bc-admin-btn bc-admin-btn--ghost bc-admin-btn--icon"
          onClick={() => {
            if (activeTab === 'users') { setQuery(''); setPage(1); fetchUsers(1) }
            else fetchBroadcasts()
          }}
          disabled={activeTab === 'users' ? loading : bcastLoading}
          title="Recarregar"
        >
          <RefreshCw size={15} className={(activeTab === 'users' ? loading : bcastLoading) ? 'bc-spin' : ''} />
        </button>
      </header>

      <nav className="bc-admin__tabs">
        <button
          className={`bc-admin__tab${activeTab === 'users' ? ' bc-admin__tab--active' : ''}`}
          onClick={() => setActiveTab('users')}
        >
          <Users size={15} />
          Usuários
        </button>
        <button
          className={`bc-admin__tab${activeTab === 'whatsapp' ? ' bc-admin__tab--active' : ''}`}
          onClick={() => setActiveTab('whatsapp')}
        >
          <MessageSquare size={15} />
          WhatsApp
        </button>
      </nav>

      {activeTab === 'users' && (
        <>
          <div className="bc-admin__search-wrap">
            <Search size={15} className="bc-admin__search-icon" />
            <input
              className="bc-admin__search"
              type="text"
              placeholder="Buscar por e-mail, nome ou User ID…"
              value={query}
              onChange={(e) => handleSearch(e.target.value)}
            />
            {query && (
              <button className="bc-admin__search-clear" onClick={() => handleSearch('')}>
                <X size={14} />
              </button>
            )}
          </div>

          {error && <p className="bc-admin-error bc-admin-error--top">{error}</p>}

          <div className="bc-admin__table-wrap">
            <table className="bc-admin__table">
              <thead>
                <tr>
                  <th>Usuário</th>
                  <th>Plano</th>
                  <th>Validade</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {loading && visibleUsers.length === 0 ? (
                  <tr><td colSpan={4} className="bc-admin__empty">Carregando…</td></tr>
                ) : visibleUsers.length === 0 ? (
                  <tr><td colSpan={4} className="bc-admin__empty">Nenhum usuário encontrado.</td></tr>
                ) : visibleUsers.map((u) => (
                  <tr key={u.id} className="bc-admin__row">
                    <td className="bc-admin__cell-user">
                      <span className="bc-admin__email">{u.email}</span>
                      {u.full_name && <span className="bc-admin__name">{u.full_name}</span>}
                    </td>
                    <td>{planBadge(u.plan_tier)}</td>
                    <td className="bc-admin__expiry">{formatExpiry(u.current_period_end)}</td>
                    <td className="bc-admin__actions">
                      <button
                        className="bc-admin-btn bc-admin-btn--pro"
                        onClick={() => setGrantTarget(u)}
                        title="Ativar Pro ou Casal"
                      >
                        <Check size={13} />
                        Ativar
                      </button>
                      {u.plan_tier !== 'free' && (
                        <button
                          className="bc-admin-btn bc-admin-btn--free"
                          onClick={() => setRevokeTarget(u)}
                          title="Revogar plano"
                        >
                          Free
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {!query && totalPages > 1 && (
            <div className="bc-admin__pagination">
              <button
                className="bc-admin-btn bc-admin-btn--ghost"
                disabled={page <= 1 || loading}
                onClick={() => handlePageChange(page - 1)}
              >
                ←
              </button>
              <span>{page} / {totalPages}</span>
              <button
                className="bc-admin-btn bc-admin-btn--ghost"
                disabled={page >= totalPages || loading}
                onClick={() => handlePageChange(page + 1)}
              >
                →
              </button>
            </div>
          )}
        </>
      )}

      {activeTab === 'whatsapp' && (
        <>
          <section className="bc-admin__section">
            <h2 className="bc-admin__section-title">
              <Send size={16} />
              Enviar Mensagem
            </h2>
            <textarea
              className="bc-admin__textarea"
              rows={4}
              placeholder="Digite a mensagem a ser enviada a todos os usuários com WhatsApp vinculado…"
              value={msgText}
              onChange={(e) => setMsgText(e.target.value)}
              disabled={msgLoading}
            />
            <div className="bc-admin__section-actions">
              <button
                className="bc-admin-btn bc-admin-btn--primary"
                disabled={msgLoading || !msgText.trim()}
                onClick={async () => {
                  await sendMessage(msgText)
                  setMsgText('')
                  fetchBroadcasts()
                }}
              >
                {msgLoading
                  ? <><RefreshCw size={14} className="bc-spin" /> Enviando…</>
                  : <><Send size={14} /> Enviar Mensagem</>
                }
              </button>
            </div>
            {sendResults && sendResults.length > 0 && (
              <div className="bc-admin__send-results">
                <p className="bc-admin__send-results-title">Resultado do último envio:</p>
                <ul className="bc-admin__send-results-list">
                  {sendResults.map((r) => (
                    <li key={r.phone} className={`bc-admin__send-result-item bc-admin__send-result-item--${r.ok ? 'ok' : 'fail'}`}>
                      <span className="bc-admin__send-result-phone">{r.phone}</span>
                      {r.ok
                        ? <span className="bc-admin__send-result-status">✓ {r.wamid ? `wamid: ${r.wamid}` : 'aceito'}</span>
                        : <span className="bc-admin__send-result-status">✗ {r.error ?? 'erro'}</span>
                      }
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </section>

          <section className="bc-admin__section">
            <h2 className="bc-admin__section-title">
              <MessageSquare size={16} />
              Histórico de Envios
            </h2>
            {bcastLoading && broadcasts.length === 0 ? (
              <p className="bc-admin__empty">Carregando…</p>
            ) : broadcasts.length === 0 ? (
              <p className="bc-admin__empty">Nenhuma mensagem enviada ainda.</p>
            ) : (
              <div className="bc-admin__bcast-list">
                {broadcasts.map((b) => (
                  <div key={b.id} className="bc-admin__bcast-item">
                    <div className="bc-admin__bcast-row">
                      <div className="bc-admin__bcast-meta">
                        <span className="bc-admin__bcast-date">{formatDateTime(b.sent_at)}</span>
                        <span className={`bc-admin__bcast-count ${b.failed_count > 0 ? 'bc-admin__bcast-count--partial' : 'bc-admin__bcast-count--ok'}`}>
                          {b.sent_count}/{b.total} enviados
                          {b.failed_count > 0 && ` · ${b.failed_count} falha(s)`}
                        </span>
                      </div>
                      <button
                        className="bc-admin-btn bc-admin-btn--ghost bc-admin__bcast-toggle"
                        onClick={() => setExpandedBroadcast(expandedBroadcast === b.id ? null : b.id)}
                      >
                        {expandedBroadcast === b.id ? '▲ Ocultar' : '▼ Detalhes'}
                      </button>
                    </div>
                    <p className="bc-admin__bcast-msg">{b.message}</p>
                    {expandedBroadcast === b.id && b.results.length > 0 && (
                      <ul className="bc-admin__send-results-list bc-admin__bcast-recipients">
                        {b.results.map((r, i) => (
                          <li key={i} className={`bc-admin__send-result-item bc-admin__send-result-item--${r.ok ? 'ok' : 'fail'}`}>
                            <span className="bc-admin__send-result-phone">{r.phone}</span>
                            {r.ok
                              ? <span className="bc-admin__send-result-status">✓ {r.wamid ? `wamid: ${r.wamid}` : 'aceito'}</span>
                              : <span className="bc-admin__send-result-status">✗ {r.error ?? 'erro'}</span>
                            }
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            )}
          </section>
        </>
      )}

      {grantTarget && (
        <GrantModal
          user={grantTarget}
          onConfirm={async (tier, cycle, end) => {
            try {
              await setPlan(grantTarget.id, tier, cycle, end)
              globalToast.success('Plano ativado.')
            } catch (err) {
              globalToast.error(getToastErrorMessage(err, 'Erro ao ativar plano.'))
              throw err
            }
          }}
          onClose={() => setGrantTarget(null)}
        />
      )}

      {revokeTarget && (
        <RevokeConfirm
          user={revokeTarget}
          onConfirm={async () => {
            try {
              await revokePlan(revokeTarget.id)
              globalToast.success('Plano revogado.')
            } catch (err) {
              globalToast.error(getToastErrorMessage(err, 'Erro ao revogar plano.'))
              throw err
            }
          }}
          onClose={() => setRevokeTarget(null)}
        />
      )}
    </main>
  )
}
